import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

// Fixed category order — must mirror backend CATEGORY_ORDER
const CATEGORY_LABELS = {
  pro: "Performance Rights Organizations",
  mechanical: "Mechanical Licensing",
  sound_recording: "Sound Recording Royalties",
  metadata: "Metadata Verification & Identifiers",
  registration: "Identifier Registration",
  artist_platforms: "Artist Platforms",
  distribution: "Distribution",
};
const CATEGORY_ORDER = [
  "pro", "mechanical", "sound_recording", "metadata",
  "registration", "artist_platforms", "distribution",
];

function hostOf(url) {
  try { return new URL(url).host.replace(/^www\./, "").toUpperCase(); }
  catch { return url.toUpperCase(); }
}

export default function Resources() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [groups, setGroups] = useState({});
  const [loadingList, setLoadingList] = useState(true);

  useEffect(() => { if (!loading && !user) nav("/auth"); }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/resources");
        setGroups(r.data);
      } finally {
        setLoadingList(false);
      }
    })();
  }, [user]);

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <div className="lq-container page-enter" data-testid="resources-page">
        <div className="eyebrow">— Resources</div>
        <h1 className="serif" style={{ fontSize: 56, lineHeight: 0.95, margin: "10px 0 18px", letterSpacing: "-0.02em" }}>
          Resources
        </h1>
        <p style={{ color: "var(--text-mute)", fontSize: 15, lineHeight: 1.65, maxWidth: 720, marginBottom: 18 }}>
          External tools and registration paths for working musicians. Every PRO, mechanical licensing body, metadata database, and artist platform Lacquer integrates with — in one place.
        </p>
        <div style={{ height: 1, background: "var(--accent)", opacity: 0.6, width: 56, marginBottom: 36 }} />

        {loadingList && <div className="data-line" style={{ padding: 24 }}>Loading…</div>}

        {!loadingList && CATEGORY_ORDER.filter((c) => groups[c]?.length).map((cat) => (
          <section key={cat} style={{ marginBottom: 52 }} data-testid={`category-${cat}`}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 18, paddingBottom: 10, borderBottom: "1px solid var(--border-soft)" }}>
              <h2 className="mono" style={{ fontSize: 11, letterSpacing: "0.20em", textTransform: "uppercase", color: "var(--text)", margin: 0 }}>
                {CATEGORY_LABELS[cat]}
              </h2>
              <span className="data-line" style={{ color: "var(--text-fade)" }}>
                {String(groups[cat].length).padStart(2, "0")}
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {groups[cat].map((r) => (
                <a
                  key={r.id}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-card"
                  data-testid={`resource-card-${r.id}`}
                >
                  <div className="serif" style={{ fontSize: 22, fontStyle: "italic", lineHeight: 1.15, marginBottom: 4 }}>
                    {r.title}
                  </div>
                  <div className="mono" style={{ fontSize: 10.5, letterSpacing: "0.16em", color: "var(--accent)", marginBottom: 10 }}>
                    {hostOf(r.url)} →
                  </div>
                  <div style={{ fontSize: 13.5, color: "var(--text-mute)", lineHeight: 1.55 }}>
                    {r.description}
                  </div>
                </a>
              ))}
            </div>
          </section>
        ))}

        {!loadingList && Object.keys(groups).length === 0 && (
          <div className="callout">No resources available yet.</div>
        )}

        <div style={{ marginTop: 48, paddingTop: 28, borderTop: "1px solid var(--border-soft)" }}>
          <p className="serif" style={{ fontStyle: "italic", fontSize: 14, color: "var(--text-mute)", textAlign: "center" }}>
            Suggest a resource? Email <a href="mailto:hello@getlacquer.app" style={{ color: "var(--accent)" }}>hello@getlacquer.app</a>
          </p>
        </div>
      </div>
    </>
  );
}
